/* eslint-disable import/no-cycle */
import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { getPaymentMethods, getLoanEmiPlans } from '@api/fetchers/payment';
import usePurchaseDetails from '@cont/Checkout/usePurchaseDetails';
import { readCookie } from '@utils/cookies';
import usePendingPayments from '@cont/Checkout/usePendingPayments';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getJWTToken } from '@api/selectors/auth';
import { commas, currencySymbolMap } from '@utils/textHelper';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import {
  getPaymentMethod,
  filterOfflineSupportedPaymentMethods
} from '@cont/Checkout/utils';
import { emptyObject } from '@constants/empty';
import useWindowSize from '@hooks/useWindowSize';
import { PAYMENT_TYPES } from '@cont/Checkout/constants';
import { QRExpiryTime } from '@cont/Checkout/UPIPayment/UPITimer';
import { isCentreSubscription, subscriptionTypes } from '@constants/index';

export const Context = createContext();

export const PaymentMethodsContextProvider = ({ children }) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn) || !!readCookie('accessToken');

  const router = useRouter();

  const { isMobile } = useWindowSize();
  const { subscriptionUID, credsApplied } = router.query;

  const {
    areStatusCallsDone,
    pendingPaymentType,
    partPaymentStatus,
    cashResponse
  } = usePendingPayments();

  const {
    purchaseDetails,
    purchaseDetailsWithoutDiscount,
    referral_code,
    validateReferralInProgress,
    referralError,
    isLoading,
    isError
  } = usePurchaseDetails();

  const isCombo = purchaseDetails
    ? purchaseDetails.subscription.childSubscriptions.length > 0
    : false;

  const [bankSelected, setBankSelected] = useState(0);
  const [selectedPayInPartsPlan, setSelectedPayInPartsPlan] = useState(null);
  const [partPaymentActive, setPartPaymentActive] = useState(false);
  const [showPartPaymentIntroDetails, setShowPartPaymentIntroDetails] =
    useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loanInterstAmt, setLoanInterestAmt] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalAmountLabel, setTotalAmountLabel] = useState();
  const [order, setOrder] = useState(null);
  const [orderExpired, setOrderExpired] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [openPriceSummary, setOpenPriceSummary] = useState(false);
  const [mwebCheckoutV2, setMwebCheckoutV2] = useState(false);
  const [qRExpiryTime, setQRExpiryTime] = useState(QRExpiryTime);
  const [generateQRClicked, setGenerateQRClicked] = useState(false);
  const [selectedMethodMeta, setSelectedMethodMeta] = useState(emptyObject);
  const meJwt = useMyInfo(getJWTToken);
  const [isExpLoading, setIsExpLoading] = useState(!!readCookie('accessToken'));
  const [upiApp, setUpiApp] = useState('');
  const [paymentPossible, setPaymentPossible] = useState(true);

  useEffect(() => {
    if (meJwt) {
      fetchABExperimentData('mweb_checkout_reskin', meJwt)
        .then(() => {
          // Setting mWebCheckoutV2 as true to enable new flow for checkout in mweb
          setMwebCheckoutV2(true);
          setIsExpLoading(false);
        })
        .catch(() => {
          setIsExpLoading(false);
        });
    } else {
      setIsExpLoading(false);
    }
  }, [meJwt]);

  useEffect(() => {
    const currency = currencySymbolMap(purchaseDetails?.subscription?.currency);
    setTotalAmountLabel(`${currency}${commas(Math.round(totalAmount))}`);
  }, [purchaseDetails?.subscription?.currency, totalAmount]);

  useEffect(() => {
    if (partPaymentStatus?.isActivePartSet) {
      setPartPaymentActive(true);
    }
  }, [partPaymentStatus, setPartPaymentActive]);

  const [arePaymentMethodsLoading, setArePaymentLoading] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pipPaymentMethods, setPipPaymentMethods] = useState([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [paymentMethodsGrouping, setPaymentMethodsGrouping] = useState([]);
  const [pipPaymentMethodsGrouping, setPipPaymentMethodsGrouping] = useState(
    []
  );
  const [loanStep, setLoanStep] = useState(0);
  const [allowRequest, setAllowRequest] = useState();

  useEffect(() => {
    const allowRequestSet = getPaymentMethod(
      availablePaymentMethods,
      PAYMENT_TYPES.LOAN
    )?.metaInfo?.loanRequestAllowed;
    setAllowRequest(allowRequestSet);
    setLoanStep(!allowRequestSet ? 2 : 0);
  }, [availablePaymentMethods]);

  useEffect(() => {
    setSelectedMethodMeta(
      getPaymentMethod(availablePaymentMethods, selectedMethod)?.metaInfo ??
        emptyObject
    );
  }, [selectedMethod, availablePaymentMethods]);

  const subscription = useMemo(() => {
    if (pendingPaymentType === 'PAC' && cashResponse?.subscriptionDetails) {
      return cashResponse?.subscriptionDetails;
    }
    if (
      pendingPaymentType === 'PIP' &&
      partPaymentStatus?.isActivePartSet &&
      partPaymentStatus?.subscription
    ) {
      return partPaymentStatus?.subscription;
    }
    return purchaseDetails?.subscription;
  }, [pendingPaymentType, purchaseDetails, partPaymentStatus, cashResponse]);

  const storeContentType = subscription?.contentTypeId;

  const isCentreSubscriptionSKU =
    subscription?.type === subscriptionTypes.CENTRE ||
    isCentreSubscription(subscription?.type, storeContentType);

  const fetchPaymentMethods = useCallback(
    (isPartPaymentFlow, noOfInstalments, setMethods, referralCode = '') => {
      setArePaymentLoading(true);
      getPaymentMethods({
        subscriptionUID,
        isPartPaymentFlow,
        noOfInstalments,
        referralCode: referralCode ?? ''
      })
        .then((result) => {
          const filteredResult = isCentreSubscriptionSKU
            ? filterOfflineSupportedPaymentMethods([...result])
            : result;

          if (filteredResult?.length) {
            const flattenedMethods = filteredResult.reduce(
              (aggr, curr) => [...aggr, ...curr.data],
              []
            );
            if (isPartPaymentFlow) {
              setPipPaymentMethodsGrouping(filteredResult);
            } else {
              setPaymentMethodsGrouping(filteredResult);
            }
            setMethods(flattenedMethods);
          }
        })
        .finally(() => setArePaymentLoading(false));
    },
    [isCentreSubscriptionSKU, subscriptionUID]
  );

  const [loanEMIList, setloanEMIList] = useState('');
  const [loanEMIListLoader, setloanEMIListLoader] = useState(true);
  const [recheckingLoanEMI, setRecheckingLoanEMI] = useState(false);
  const [isLoanRecheck, setIsLoanRecheck] = useState(false);

  const fetchLoanEmiPlans = ({ amount, phone }, recheck) => {
    setloanEMIListLoader(true);
    setloanEMIListLoader(true);
    if (recheck) {
      setIsLoanRecheck(true);
      setRecheckingLoanEMI(true);
    } else {
      setRecheckingLoanEMI(false);
    }
    getLoanEmiPlans({
      amount,
      phone
    })
      .then((result) => {
        setloanEMIList(result);
      })
      .catch(() => {
        setloanEMIList([]);
      })
      .finally(() => {
        setloanEMIListLoader(false);
        setRecheckingLoanEMI(false);
      });
  };

  const isActivePartPaymentFlow =
    partPaymentActive || partPaymentStatus?.isActivePartSet;

  useEffect(() => {
    if (isLoggedIn && !isActivePartPaymentFlow) {
      fetchPaymentMethods(false, 1, setPaymentMethods);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, subscriptionUID, isActivePartPaymentFlow]);

  useEffect(() => {
    if (isLoggedIn && referral_code && !isActivePartPaymentFlow) {
      fetchPaymentMethods(false, 1, setPaymentMethods, referral_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, subscriptionUID, referral_code, isActivePartPaymentFlow]);

  useEffect(() => {
    if (isLoggedIn && isActivePartPaymentFlow) {
      const instalmentCount =
        selectedPayInPartsPlan?.length ||
        partPaymentStatus?.partPayments?.length;
      if (!instalmentCount) return;
      fetchPaymentMethods(
        true,
        instalmentCount,
        setPipPaymentMethods,
        selectedPayInPartsPlan ? referral_code : ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedIn,
    subscriptionUID,
    isActivePartPaymentFlow,
    referral_code,
    selectedPayInPartsPlan,
    partPaymentStatus?.partPayments?.length
  ]);

  useEffect(() => {
    if (partPaymentActive) setAvailablePaymentMethods(pipPaymentMethods);
    else setAvailablePaymentMethods(paymentMethods);
  }, [paymentMethods, pipPaymentMethods, partPaymentActive]);

  const data = {
    loanEMIList,
    merchantId: process.env.JUSPAY_MERCHANT_ID,
    isCombo,
    subscription,
    subscriptionWithoutDiscount:
      purchaseDetailsWithoutDiscount?.subscription || subscription,
    referralError,
    isLoading: isLoading || isExpLoading,
    isError: subscription ? null : isError,
    validateReferralInProgress,
    creditsApplied: credsApplied === 'true' && isLoggedIn,
    bankSelected,
    setBankSelected,
    totalAmount,
    setTotalAmount,
    totalAmountLabel,
    selectedPayInPartsPlan,
    setSelectedPayInPartsPlan,
    partPaymentActive,
    setPartPaymentActive,
    showPartPaymentIntroDetails,
    setShowPartPaymentIntroDetails,
    selectedMethod,
    setSelectedMethod,
    loanInterstAmt,
    setLoanInterestAmt,
    availablePaymentMethods,
    setAvailablePaymentMethods,
    paymentMethodsGrouping: isActivePartPaymentFlow
      ? pipPaymentMethodsGrouping
      : paymentMethodsGrouping,
    order,
    setOrder,
    isCreatingOrder,
    setIsCreatingOrder,
    arePaymentMethodsLoading,
    areStatusCallsDone,
    pendingPaymentType,
    partPaymentStatus,
    cashResponse,
    openPriceSummary,
    setOpenPriceSummary,
    mwebCheckoutV2: mwebCheckoutV2 && isMobile,
    selectedMethodMeta,
    loanStep,
    setLoanStep,
    allowRequest,
    upiApp,
    setUpiApp,
    paymentPossible,
    setPaymentPossible,
    setOrderExpired,
    orderExpired,
    setQRExpiryTime,
    qRExpiryTime,
    generateQRClicked,
    setGenerateQRClicked,
    fetchLoanEmiPlans,
    loanEMIListLoader,
    isLoanRecheck,
    recheckingLoanEMI
  };
  return <Context.Provider value={data}>{children}</Context.Provider>;
};

PaymentMethodsContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};
